import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
} from "@chakra-ui/react";
import { ChevronDownIcon, CheckIcon } from "@chakra-ui/icons";
import React, { useEffect, useRef } from "react";
import { CommonTooltip } from "@root/components/common/tooltip/CommonTooltip";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { ROUTE_NAMES } from "@root/constants";

interface ModelSelectorProps {
  label?: string;
  options: { label: string; type: number; key: string }[];
  value: number;
  onChange: (value: number) => void;
  onCustomChange?: (value: number) => void;
  disabled?: boolean;
  tooltipLabel?: string;
  onMenuClose?: () => void;
}

export default function ModelSelector({
  label = "SELECT MODEL",
  options,
  value,
  onChange,
  onCustomChange,
  disabled = false,
  tooltipLabel,
  onMenuClose,
}: ModelSelectorProps) {
  const STORAGE_KEY = "model-selector-last-selected-label";
  const prevOptionsRef = useRef<{ label: string; type: number; key: string }[]>([]);
  const prevValueRef = useRef<number>(value);

  useEffect(() => {
    // Check if this is the first render (prevOptionsRef is empty)
    const isFirstRender = prevOptionsRef.current.length === 0;

    // Check if options have changed by comparing lengths and labels only
    const optionsChanged =
      isFirstRender ||
      prevOptionsRef.current.length !== options.length ||
      prevOptionsRef.current.some(
        (prevOpt, index) =>
          !options[index] ||
          prevOpt.label !== options[index].label
      );

    if (optionsChanged) {
      let labelToMatch: string | null = null;

      if (isFirstRender) {
        // On first render, try to get the last selected label from localStorage
        const storedLabel = localStorage.getItem(STORAGE_KEY);

        // Check if current value exists in options
        const currentValueOption = options.find((opt) => opt.type === value);

        if (currentValueOption) {
          // Current value exists, use its label
          labelToMatch = currentValueOption.label;
        } else if (storedLabel) {
          // Current value doesn't exist, use stored label from previous component
          labelToMatch = storedLabel;
        }
      } else {
        // On subsequent renders, get the label of the previously selected option
        const previousSelectedOption = prevOptionsRef.current.find(
          (opt) => opt.type === prevValueRef.current
        );
        labelToMatch = previousSelectedOption?.label || localStorage.getItem(STORAGE_KEY);
      }

      if (labelToMatch) {
        // Check if an option with the same label exists in the new options
        const matchingOption = options.find((opt) => opt.label === labelToMatch);

        if (matchingOption) {
          // Select the option with the same label
          onChange(matchingOption.type);
          localStorage.setItem(STORAGE_KEY, matchingOption.label);
        } else if (options.length > 0) {
          // Select the first option if no matching label found
          onChange(options[0].type);
          localStorage.setItem(STORAGE_KEY, options[0].label);
        }
      } else if (options.length > 0) {
        // If no label to match, select first option
        onChange(options[0].type);
        localStorage.setItem(STORAGE_KEY, options[0].label);
      }

      // Update options ref
      prevOptionsRef.current = options;
    }

    // Always update value ref and store label in localStorage
    const currentOption = options.find((opt) => opt.type === value);
    if (currentOption) {
      localStorage.setItem(STORAGE_KEY, currentOption.label);
    }
    prevValueRef.current = value;
  }, [options, value]);

  const menuContent = (
    <Box position="relative" minW="0" flexShrink={1}>
      <Menu
        matchWidth
        isLazy
        onClose={() => {
          // Call onMenuClose callback when menu closes to reset tooltip hover state
          onMenuClose?.();
        }}
        modifiers={[
          {
            name: "computeStyles",
            options: { adaptive: false },
          },
        ]}
      >
        <MenuButton
          as={Button}
          height={{ base: "36px", md: "40px" }}
          width={{ base: "140px", md: "180px", lg: "200px" }}
          bg="#FFFFFF"
          fontSize={{ base: "12px", md: "14px" }}
          fontWeight="#1A1A1A"
          rightIcon={<ChevronDownIcon />}
          borderRadius="lg"
          variant="outline"
          textAlign="start"
          px={{ base: 2, md: 4 }}
          isDisabled={disabled}
          opacity={disabled ? 0.5 : 1}
          cursor={disabled ? "not-allowed" : "pointer"}
          transition="opacity 0.2s ease"
          _hover={disabled ? {} : { bg: "#E6E6E6" }}
          _active={disabled ? {} : { bg: "#E6E6E6" }}
        >
          {options.find((o) => o.type === value)?.label || label}
        </MenuButton>

        <MenuList
          fontSize={{ base: "11px", md: "12px" }}
          fontWeight="#1A1A1A"
          w="full"
          minW="unset"
        >
          {options?.map((opt) => (
            <MenuItem
              key={opt?.type}
              onClick={() => {
                if (!disabled) {
                  onChange(opt?.type); // Always update the model state
                  onCustomChange?.(opt?.type); // Only fire on user clicks
                }
              }}
              w="full"
              py={{ base: 2, md: 3 }}
              px={{ base: 2, md: 3 }}
              isDisabled={disabled}
            >
              <Flex align="center" justify="space-between" w="full">
                {opt?.label}
                {value === opt?.type && <CheckIcon />}
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );

  if (disabled && tooltipLabel) {
    return <CommonTooltip label={tooltipLabel}>{menuContent}</CommonTooltip>;
  }

  return menuContent;
}
